import React, { useState, useEffect, useCallback, useRef } from 'react';
import { taxLines } from './lib/taxLines';
import { daysBetween, describeRateLine } from './lib/rentalDays';
import { useParams, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://ivision-crm-backend-production.up.railway.app';

const C = {
  bg:      '#1a1c28',
  card:    '#1e2030',
  card2:   '#2a2d40',
  border:  '#2e3150',
  text:    '#e8eaf0',
  muted:   '#4a5568',
  accent:  '#f97316',
  blue:    '#3b82f6',
  green:   '#34d399',
  red:     '#f87171',
  amber:   '#f59e0b',
  purple:  '#a78bfa',
};

const STATUS_COLOR = { reserved:'#a78bfa', started:'#3b82f6', returned:'#34d399', cancelled:'#4a5568', draft:'#64748b', archived:'#4a5568' };
const STATUS_LABEL = { reserved:'Reserved', started:'Active', returned:'Returned', cancelled:'Cancelled', draft:'Draft', archived:'Archived' };
const PAYMENT_COLOR = { paid:'#34d399', partially_paid:'#f59e0b', payment_due:'#f87171', process_deposit:'#a78bfa', overpaid:'#60a5fa' };
const PAYMENT_LABEL = { paid:'Paid', partially_paid:'Partially Paid', payment_due:'Balance Due', process_deposit:'Deposit Req.', overpaid:'Overpaid' };

function fmt(n) { return '$' + Number(n||0).toFixed(2); }
function fmtDate(d) { if (!d) return '—'; return new Date(d + 'T12:00:00').toLocaleDateString('en-US', { month:'short', day:'numeric', year:'numeric' }); }

export default function RentalClientPortal() {
  const { subdomain }       = useParams();
  const [searchParams]      = useSearchParams();
  const storageKey          = `rental_portal_${subdomain}`;

  const [token,    setToken]    = useState(() => localStorage.getItem(storageKey) || '');
  const [client,   setClient]   = useState(null);
  const [business, setBusiness] = useState(null);
  const [tab,      setTab]      = useState('orders');
  const [loading,  setLoading]  = useState(true);
  const [orders,   setOrders]   = useState([]);
  const [selOrder, setSelOrder] = useState(null);
  const [signing,  setSigning]  = useState(false);
  const [signName, setSignName] = useState('');
  const [signErr,  setSignErr]  = useState('');
  const [signDone, setSignDone] = useState(false);
  const canvasRef = useRef(null);
  const isDrawing = useRef(false);
  const [flash,    setFlash]    = useState(null);
  const [stats,    setStats]    = useState(null);
  const [showAcct,    setShowAcct]    = useState(false);
  const [acctName,    setAcctName]    = useState('');
  const [acctPhone,   setAcctPhone]   = useState('');
  const [acctEmail,   setAcctEmail]   = useState('');
  const [acctSaving,  setAcctSaving]  = useState(false);
  const [acctMsg,     setAcctMsg]     = useState(null);

  // Login state
  const [step,    setStep]    = useState('email');
  const [email,   setEmail]   = useState('');
  const [code,    setCode]    = useState('');
  const [lErr,    setLErr]    = useState('');
  const [lMsg,    setLMsg]    = useState('');
  const [lLoading,setLLoading]= useState(false);

  const showFlash = (msg, type='success') => {
    setFlash({ msg, type });
    setTimeout(() => setFlash(null), 4000);
  };

  // Payment return params handled by dedicated success screen above

  const portalFetch = useCallback(async (path, opts = {}) => {
    const res = await fetch(API + path, {
      ...opts,
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}`, ...(opts.headers || {}) },
    });
    if (res.status === 401) { logout(); throw new Error('Session expired'); }
    return res.json();
  }, [token]);

  const logout = () => {
    localStorage.removeItem(storageKey);
    setToken(''); setClient(null); setOrders([]); setLoading(false);
  };

  const loadData = useCallback(async () => {
    try {
      const [meData, ordersData, statsData] = await Promise.all([
        portalFetch('/api/rental-portal/me'),
        portalFetch('/api/rental-portal/orders'),
        portalFetch('/api/rental-portal/stats'),
      ]);
      if (meData.success) { setClient(meData.client); setBusiness(meData.business); }
      if (ordersData.success) setOrders(ordersData.orders || []);
      if (statsData.success) setStats(statsData);
    } catch {}
    setLoading(false);
  }, [portalFetch]);

  useEffect(() => {
    if (token) loadData();
    else setLoading(false);
  }, [token, loadData]);

  const sendCode = async () => {
    setLLoading(true); setLErr(''); setLMsg('');
    try {
      const r = await fetch(`${API}/api/rental-portal/send-code`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, subdomain }),
      });
      const d = await r.json();
      if (d.success) { setStep('code'); setLMsg('Code sent — check your email'); }
      else setLErr(d.error || 'Failed to send code');
    } catch { setLErr('Network error'); }
    setLLoading(false);
  };

  const verifyCode = async () => {
    setLLoading(true); setLErr('');
    try {
      const r = await fetch(`${API}/api/rental-portal/verify-code`, {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({ email, code, subdomain }),
      });
      const d = await r.json();
      if (d.success) {
        localStorage.setItem(storageKey, d.token);
        setToken(d.token);
        setClient(d.client);
        setBusiness(d.business);
        setLoading(true);
      } else setLErr(d.error || 'Invalid code');
    } catch { setLErr('Network error'); }
    setLLoading(false);
  };

  const payOrder = async (order, payment_type = 'full') => {
    try {
      const r = await portalFetch(`/api/rental-portal/orders/${order.id}/pay`, {
        method: 'POST',
        body: JSON.stringify({ payment_type }),
      });
      if (r.url) window.location.href = r.url;
      else showFlash(r.error || 'Payment setup failed', 'error');
    } catch { showFlash('Payment error', 'error'); }
  };

  // ── Sign contract ────────────────────────────────────────────
  async function submitSignature(order) {
    setSignErr('');
    if (!signName.trim()) { setSignErr('Please type your full name'); return; }
    const canvas = canvasRef.current;
    if (!canvas) { setSignErr('Signature pad not ready'); return; }
    const blank = document.createElement('canvas');
    blank.width = canvas.width; blank.height = canvas.height;
    if (canvas.toDataURL() === blank.toDataURL()) { setSignErr('Please draw your signature'); return; }
    const sigData = canvas.toDataURL('image/png');
    try {
      const res = await fetch(API + '/api/rental-portal/orders/' + order.id + '/sign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + token },
        body: JSON.stringify({ signature_data: sigData, full_name: signName.trim() }),
      });
      const data = await res.json();
      if (!res.ok) { setSignErr(data.error || 'Failed to submit signature'); return; }
      setSignDone(true);
      setTimeout(() => {
        setSigning(false); setSignDone(false); setSignName('');
        setSelOrder(o => o ? { ...o, signed_at: data.signed_at } : o);
        if (canvasRef.current) canvasRef.current.getContext('2d').clearRect(0,0,canvasRef.current.width,canvasRef.current.height);
      }, 1800);
    } catch (e) { setSignErr(e.message); }
  }

  function clearSig() {
    if (canvasRef.current) canvasRef.current.getContext('2d').clearRect(0,0,canvasRef.current.width,canvasRef.current.height);
  }
  function sigStart(e) {
    isDrawing.current = true;
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.beginPath();
    ctx.moveTo(((e.touches?e.touches[0].clientX:e.clientX)-rect.left)*scaleX, ((e.touches?e.touches[0].clientY:e.clientY)-rect.top)*scaleY);
  }
  function sigDraw(e) {
    if (!isDrawing.current) return; e.preventDefault();
    const canvas = canvasRef.current; if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    ctx.lineWidth=2; ctx.lineCap='round'; ctx.strokeStyle='#e2e8f0';
    ctx.lineTo(((e.touches?e.touches[0].clientX:e.clientX)-rect.left)*scaleX, ((e.touches?e.touches[0].clientY:e.clientY)-rect.top)*scaleY);
    ctx.stroke();
  }
  function sigEnd() { isDrawing.current = false; }

  // ── PAYMENT SUCCESS SCREEN (before login wall) ──────────────
  const pmParam  = searchParams.get('payment');
  const oidParam = searchParams.get('order');
  if (pmParam === 'success' && (!token || (!client && !loading))) {
    return (
      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
        <div style={{ width:'100%', maxWidth:420, textAlign:'center' }}>
          <div style={{ fontSize:72, marginBottom:16 }}>✅</div>
          <div style={{ fontSize:28, fontWeight:900, color:C.text, marginBottom:8 }}>Payment Received!</div>
          <div style={{ fontSize:15, color:'#94a3b8', marginBottom:6, lineHeight:1.6 }}>
            Your payment for Order <span style={{ color:C.accent, fontWeight:700 }}>#{oidParam}</span> was processed successfully.
          </div>
          <div style={{ fontSize:13, color:C.muted, marginBottom:32 }}>📧 A receipt has been sent to your email.</div>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'20px 24px', marginBottom:16 }}>
            <div style={{ fontSize:13, color:C.muted, marginBottom:12 }}>Sign in to view your order details</div>
            <button onClick={() => { window.history.replaceState({}, '', window.location.pathname); window.location.reload(); }}
              style={{ padding:'12px 28px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontWeight:800, fontSize:14, cursor:'pointer' }}>
              View My Orders →
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── LOGIN SCREEN ────────────────────────────────────────────
  if (!token || (!client && !loading)) {
    return (
      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', padding:20, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
        <div style={{ width:'100%', maxWidth:400 }}>
          {/* Logo */}
          <div style={{ textAlign:'center', marginBottom:32 }}>
            <div style={{ width:56, height:56, background:'rgba(249,115,22,0.1)', border:'1px solid rgba(249,115,22,0.3)', borderRadius:16, display:'inline-flex', alignItems:'center', justifyContent:'center', fontSize:26, marginBottom:16 }}>🏗️</div>
            <div style={{ fontSize:24, fontWeight:900, color:C.text, letterSpacing:'-0.5px' }}>
              {business?.name || 'Customer Portal'}
            </div>
            <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>Client Portal</div>
          </div>

          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, padding:'32px 28px', boxShadow:'0 20px 60px rgba(0,0,0,0.4)' }}>
            {lErr && (
              <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:16, color:C.red, fontSize:13 }}>
                {lErr}
              </div>
            )}
            {lMsg && (
              <div style={{ background:'rgba(52,211,153,0.1)', border:'1px solid rgba(52,211,153,0.3)', borderRadius:8, padding:'10px 14px', marginBottom:16, color:C.green, fontSize:13 }}>
                ✓ {lMsg}
              </div>
            )}

            {step === 'email' ? (
              <>
                <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>Sign in to your account</div>
                <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Enter your email to receive a login code</div>
                <input
                  autoFocus type="email" placeholder="your@email.com"
                  value={email} onChange={e => setEmail(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && email && sendCode()}
                  style={{ width:'100%', padding:'12px 14px', borderRadius:10, border:`1px solid ${C.border}`, background:'#181928', color:C.text, fontSize:14, boxSizing:'border-box', outline:'none', marginBottom:16 }}
                />
                <button disabled={!email || lLoading} onClick={sendCode}
                  style={{ width:'100%', padding:'13px', borderRadius:10, border:'none', background:C.accent, color:'#fff', fontWeight:800, fontSize:14, cursor:'pointer', opacity:(!email||lLoading)?0.5:1 }}>
                  {lLoading ? 'Sending...' : 'Send Login Code →'}
                </button>
              </>
            ) : (
              <>
                <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:6 }}>Enter your code</div>
                <div style={{ fontSize:13, color:C.muted, marginBottom:20 }}>Check your email at <strong style={{color:C.text}}>{email}</strong></div>
                <input
                  autoFocus type="text" inputMode="numeric" placeholder="000000"
                  value={code} onChange={e => setCode(e.target.value.replace(/\D/g,'').slice(0,6))}
                  onKeyDown={e => e.key === 'Enter' && code.length === 6 && verifyCode()}
                  style={{ width:'100%', padding:'16px 14px', borderRadius:10, border:`1px solid ${C.border}`, background:'#181928', color:C.text, fontSize:28, fontWeight:800, letterSpacing:10, textAlign:'center', boxSizing:'border-box', outline:'none', marginBottom:16 }}
                />
                <button disabled={code.length < 6 || lLoading} onClick={verifyCode}
                  style={{ width:'100%', padding:'13px', borderRadius:10, border:'none', background:C.green, color:'#064e3b', fontWeight:800, fontSize:14, cursor:'pointer', opacity:(code.length<6||lLoading)?0.5:1 }}>
                  {lLoading ? 'Verifying...' : '✓ Sign In'}
                </button>
                <button onClick={() => { setStep('email'); setCode(''); setLErr(''); }}
                  style={{ width:'100%', marginTop:10, padding:'10px', background:'none', border:'none', color:C.muted, fontSize:13, cursor:'pointer' }}>
                  ← Use different email
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ minHeight:'100vh', background:C.bg, display:'flex', alignItems:'center', justifyContent:'center', fontFamily:"'DM Sans',system-ui,sans-serif" }}>
        <div style={{ textAlign:'center' }}>
          <div style={{ fontSize:32, marginBottom:12 }}>🏗️</div>
          <div style={{ color:C.muted, fontSize:14 }}>Loading your account...</div>
        </div>
      </div>
    );
  }

  // ── ORDER DETAIL VIEW ───────────────────────────────────────
  if (selOrder) {
    const o = selOrder;
    const balance = Math.max(0, Number(o.total||0) - Number(o.amount_paid||0));
    return (
      <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
        <Header business={business} client={client} onLogout={logout} onAccountSettings={() => { setAcctMsg(null); setShowAcct(true); }} />
        <div style={{ maxWidth:680, margin:'0 auto', padding:'24px 16px' }}>
          <button onClick={() => setSelOrder(null)}
            style={{ background:'none', border:'none', color:C.muted, cursor:'pointer', fontSize:13, marginBottom:20, display:'flex', alignItems:'center', gap:6 }}>
            ← Back to Orders
          </button>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, overflow:'hidden', marginBottom:16 }}>
            <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
              <div>
                <div style={{ fontSize:18, fontWeight:800, color:C.text }}>Order #{o.id}</div>
                <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>Created {fmtDate(o.created_at)}</div>
              </div>
              <div style={{ display:'flex', gap:8 }}>
                <span style={{ padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:700, background:`${STATUS_COLOR[o.status]}20`, color:STATUS_COLOR[o.status] }}>{STATUS_LABEL[o.status]}</span>
                <span style={{ padding:'5px 12px', borderRadius:20, fontSize:11, fontWeight:700, background:`${PAYMENT_COLOR[o.payment_status]}20`, color:PAYMENT_COLOR[o.payment_status] }}>{PAYMENT_LABEL[o.payment_status]}</span>
              </div>
            </div>
            <div style={{ padding:'20px 24px' }}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:16, marginBottom:20 }}>
                {[['📅 Pickup Date', fmtDate(o.from_date)], ['📅 Return Date', fmtDate(o.to_date)], ['🚚 Fulfillment', (o.fulfillment_type||'pickup').charAt(0).toUpperCase()+(o.fulfillment_type||'pickup').slice(1)], ['📍 Address', o.delivery_address || (o.fulfillment_type==='delivery'?'—':'Pickup')]].map(([label,val])=>(
                  <div key={label} style={{ background:C.card2, borderRadius:10, padding:'12px 14px' }}>
                    <div style={{ fontSize:11, color:C.muted, marginBottom:4 }}>{label}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:C.text }}>{val}</div>
                  </div>
                ))}
              </div>

              {(o.rental_order_items||[]).length > 0 && (
                <div style={{ marginBottom:20 }}>
                  <div style={{ fontSize:12, fontWeight:700, color:C.accent, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:10 }}>Equipment</div>
                  {o.rental_order_items.map((item, i) => (
                    <div key={i} style={{ display:'flex', justifyContent:'space-between', padding:'10px 0', borderBottom:`1px solid ${C.border}` }}>
                      <div>
                        <div style={{ fontSize:14, fontWeight:600, color:C.text }}>{item.item_name}</div>
                        <div style={{ fontSize:12, color:C.muted }}>{describeRateLine(item, daysBetween(o.from_date, o.to_date))}</div>
                      </div>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{fmt(item.total_price)}</div>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ background:C.card2, borderRadius:10, padding:'14px 16px' }}>
                {[...taxLines(o).map(r => [r.label, r.kind === 'discount' ? '-' + fmt(-r.value) : fmt(r.value), r.kind === 'discount' ? C.green : (r.kind === 'total' ? C.text : undefined)]),
                  ['Amount Paid', fmt(o.amount_paid||0), C.green], ['Balance Due', fmt(balance), balance>0?C.red:C.green]].map(([label,val,color])=>(
                  <div key={label} style={{ display:'flex', justifyContent:'space-between', padding:'6px 0' }}>
                    <span style={{ fontSize:13, color:C.muted }}>{label}</span>
                    <span style={{ fontSize:13, fontWeight:700, color:color||C.text }}>{val}</span>
                  </div>
                ))}
              </div>

              {balance > 0 && o.status !== 'cancelled' && (
                <div style={{ marginTop:16, display:'flex', gap:10 }}>
                  <button onClick={() => payOrder(o, 'full')}
                    style={{ flex:1, padding:'12px', borderRadius:10, border:'none', background:C.accent, color:'#fff', fontWeight:800, fontSize:14, cursor:'pointer' }}>
                    💳 Pay {fmt(balance)}
                  </button>
                  {Number(o.deposit_amount||0) > 0 && Number(o.amount_paid||0) === 0 && (
                    <button onClick={() => payOrder(o, 'deposit')}
                      style={{ flex:1, padding:'12px', borderRadius:10, border:`1px solid ${C.border}`, background:'transparent', color:C.text, fontWeight:700, fontSize:13, cursor:'pointer' }}>
                      Pay Deposit ({fmt(o.deposit_amount)})
                    </button>
                  )}
                </div>
              )}

              {/* Contract signing */}
              {!o.signed_at && ['reserved','started'].includes(o.status) && (
                <button onClick={() => { setSigning(true); setSignErr(''); setSignName(''); setSignDone(false); }}
                  style={{ display:'flex', alignItems:'center', justifyContent:'center', gap:8, marginTop:16, width:'100%', padding:'13px', borderRadius:10, border:`2px solid ${C.purple}`, background:`${C.purple}18`, color:C.purple, fontSize:14, fontWeight:800, cursor:'pointer' }}>
                  ✍️ Sign Rental Agreement
                </button>
              )}
              {o.signed_at && (
                <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:16, padding:'12px 14px', borderRadius:10, border:`1px solid ${C.green}40`, background:`${C.green}10`, color:C.green, fontSize:13, fontWeight:600 }}>
                  ✅ Agreement signed on {fmtDate(o.signed_at)}
                </div>
              )}

              {/* Signature Modal */}
              {signing && !o.signed_at && (
                <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:9000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
                  <div style={{ background:'#252838', border:'1px solid #3a3f60', borderRadius:16, width:'100%', maxWidth:480, padding:24 }}>
                    {signDone ? (
                      <div style={{ textAlign:'center', padding:'32px 0' }}>
                        <div style={{ fontSize:56, marginBottom:12 }}>✅</div>
                        <div style={{ fontSize:20, fontWeight:800, color:C.green, marginBottom:6 }}>Contract Signed!</div>
                        <div style={{ fontSize:13, color:C.muted }}>Your rental agreement has been submitted.</div>
                      </div>
                    ) : (<>
                      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
                        <div style={{ fontSize:16, fontWeight:800, color:'#e2e8f0' }}>✍️ Sign Rental Agreement</div>
                        <button onClick={() => setSigning(false)} style={{ background:'none', border:'none', color:'#4a5568', cursor:'pointer', fontSize:18 }}>✕</button>
                      </div>
                      <div style={{ fontSize:12, color:'#64748b', marginBottom:16, lineHeight:1.6 }}>By signing, you agree to the rental terms for Order #{o.id}. This signature is legally binding.</div>
                      <div style={{ marginBottom:14 }}>
                        <label style={{ fontSize:11, color:'#64748b', fontWeight:600, display:'block', marginBottom:6, textTransform:'uppercase', letterSpacing:'.4px' }}>Full Legal Name *</label>
                        <input value={signName} onChange={e => setSignName(e.target.value)} placeholder="Type your full name"
                          style={{ width:'100%', padding:'10px 12px', borderRadius:8, border:'1px solid #3a3f60', background:'#1e2235', color:'#e2e8f0', fontSize:13, boxSizing:'border-box' }} />
                      </div>
                      <div style={{ marginBottom:14 }}>
                        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
                          <label style={{ fontSize:11, color:'#64748b', fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px' }}>Draw Signature *</label>
                          <button onClick={clearSig} style={{ fontSize:11, color:'#94a3b8', background:'none', border:'none', cursor:'pointer' }}>Clear</button>
                        </div>
                        <canvas ref={canvasRef} width={480} height={130}
                          style={{ width:'100%', height:130, borderRadius:8, border:'1px solid #3a3f60', background:'#070810', cursor:'crosshair', touchAction:'none', display:'block' }}
                          onMouseDown={sigStart} onMouseMove={sigDraw} onMouseUp={sigEnd} onMouseLeave={sigEnd}
                          onTouchStart={sigStart} onTouchMove={sigDraw} onTouchEnd={sigEnd} />
                      </div>
                      {signErr && <div style={{ color:'#f87171', fontSize:12, marginBottom:12, padding:'8px 12px', background:'rgba(239,68,68,0.1)', borderRadius:7 }}>{signErr}</div>}
                      <button onClick={() => submitSignature(o)}
                        style={{ width:'100%', padding:'13px', borderRadius:10, border:'none', background:C.purple, color:'#fff', fontWeight:800, fontSize:14, cursor:'pointer', marginTop:4 }}>
                        Submit Signature
                      </button>
                    </>)}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── MAIN PORTAL ─────────────────────────────────────────────
  const activeOrders  = orders.filter(o => ['reserved','started'].includes(o.status));
  const pastOrders    = orders.filter(o => ['returned','cancelled','archived'].includes(o.status));
  const totalOwed     = orders.reduce((s,o) => s + Math.max(0, Number(o.total||0) - Number(o.amount_paid||0)), 0);
  const hasUnsigned   = orders.filter(o => !o.signed_at && ['reserved','started'].includes(o.status) && o.contract_pdf_url);

  function AccountModal() {
    if (!showAcct) return null;
    const save = async () => {
      if (!acctName.trim()) { setAcctMsg({ ok:false, text:'Name is required.' }); return; }
      if (!acctEmail.trim()) { setAcctMsg({ ok:false, text:'Email is required.' }); return; }
      setAcctSaving(true); setAcctMsg(null);
      try {
        const r = await fetch(API+'/api/rentals/portal/update-profile', {
          method:'POST',
          headers:{'Content-Type':'application/json','Authorization':'Bearer '+token},
          body: JSON.stringify({ name:acctName.trim(), phone:acctPhone.trim(), email:acctEmail.trim() })
        });
        const d = await r.json();
        if (d.success) {
          setAcctMsg({ ok:true, text:'Profile updated successfully.' });
          setClient(c => ({ ...c, name:acctName.trim(), phone:acctPhone.trim(), email:acctEmail.trim() }));
        } else { setAcctMsg({ ok:false, text: d.error||'Failed to update.' }); }
      } catch(e) { setAcctMsg({ ok:false, text:'Network error.' }); }
      setAcctSaving(false);
    };
    return (
      <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.7)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:20 }}
        onClick={e => { if (e.target===e.currentTarget) setShowAcct(false); }}>
        <div style={{ background:C.card, border:'1px solid '+C.border, borderRadius:14, padding:24, width:'100%', maxWidth:420, boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
            <div style={{ fontSize:16, fontWeight:800, color:C.text }}>Account Settings</div>
            <button onClick={() => setShowAcct(false)} style={{ background:'none', border:'none', color:C.muted, fontSize:20, cursor:'pointer', lineHeight:1 }}>x</button>
          </div>
          {acctMsg && (
            <div style={{ padding:'10px 14px', borderRadius:8, marginBottom:16, background:acctMsg.ok?'rgba(52,211,153,0.1)':'rgba(248,113,113,0.1)', border:'1px solid '+(acctMsg.ok?C.green:C.red), color:acctMsg.ok?C.green:C.red, fontSize:13 }}>
              {acctMsg.text}
            </div>
          )}
          {[
            { label:'Display Name', val:acctName, set:setAcctName, type:'text', placeholder:'Your name' },
            { label:'Phone Number', val:acctPhone, set:setAcctPhone, type:'tel', placeholder:'(702) 555-0000' },
            { label:'Email Address', val:acctEmail, set:setAcctEmail, type:'email', placeholder:'you@example.com', note:'This is your login email - changing it takes effect next sign-in.' },
          ].map(({ label, val, set, type, placeholder, note }) => (
            <div key={label} style={{ marginBottom:14 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.muted, textTransform:'uppercase', letterSpacing:'.5px', marginBottom:5 }}>{label}</div>
              <input type={type} value={val} onChange={e=>set(e.target.value)} placeholder={placeholder}
                style={{ width:'100%', background:C.card2, border:'1px solid '+C.border, borderRadius:7, padding:'9px 12px', color:C.text, fontSize:13, boxSizing:'border-box' }} />
              {note && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{note}</div>}
            </div>
          ))}
          <div style={{ display:'flex', gap:10, marginTop:20 }}>
            <button onClick={() => setShowAcct(false)}
              style={{ flex:1, padding:'10px', borderRadius:8, border:'1px solid '+C.border, background:'transparent', color:C.muted, fontSize:13, cursor:'pointer', fontWeight:600 }}>
              Cancel
            </button>
            <button onClick={save} disabled={acctSaving}
              style={{ flex:2, padding:'10px', borderRadius:8, border:'none', background:C.accent, color:'#fff', fontSize:13, cursor:'pointer', fontWeight:700, opacity:acctSaving?0.6:1 }}>
              {acctSaving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <AccountModal />
      <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'DM Sans',system-ui,sans-serif" }}>
      {flash && (
        <div style={{ position:'fixed', top:16, right:16, zIndex:9999, padding:'12px 20px', borderRadius:10, background:flash.type==='error'?'#3b0000':'#0a2e18', border:`1px solid ${flash.type==='error'?C.red:C.green}`, color:flash.type==='error'?C.red:C.green, fontSize:13, fontWeight:700, boxShadow:'0 4px 20px rgba(0,0,0,0.4)' }}>
          {flash.msg}
        </div>
      )}

      <Header business={business} client={client} onLogout={logout} />

      <div style={{ maxWidth:720, margin:'0 auto', padding:'24px 16px' }}>

        {/* Welcome banner */}
        <div style={{ background:`linear-gradient(135deg, rgba(249,115,22,0.08) 0%, rgba(59,130,246,0.05) 100%)`, border:`1px solid rgba(249,115,22,0.15)`, borderRadius:14, padding:'20px 24px', marginBottom:24, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:C.text }}>Hey, {client?.name?.split(' ')[0] || 'there'} 👋</div>
            {client?.is_business_user && (
              <div style={{ fontSize:13, color:C.muted, marginTop:2 }}>
                <span style={{ color:C.accent, fontWeight:600 }}>{client?.company}</span> · <span style={{ textTransform:'capitalize' }}>{client?.role || 'member'}</span>
                {client?.role === 'admin' && (
                  <span style={{ marginLeft:4, color:C.muted, cursor:'help' }} title="Can view and manage all orders under this business account">ⓘ</span>
                )}
              </div>
            )}
            <div style={{ fontSize:13, color:C.muted, marginTop:3 }}>
              {activeOrders.length > 0 ? `You have ${activeOrders.length} active rental${activeOrders.length>1?'s':''}` : 'No active rentals right now'}
            </div>
          </div>
          {totalOwed > 0 && (
            <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.2)', borderRadius:10, padding:'10px 16px', textAlign:'right' }}>
              <div style={{ fontSize:11, color:C.red, fontWeight:700, textTransform:'uppercase', letterSpacing:'.5px' }}>Balance Due</div>
              <div style={{ fontSize:22, fontWeight:900, color:C.red }}>{fmt(totalOwed)}</div>
            </div>
          )}
        </div>

        {/* Unsigned contract alert */}
        {hasUnsigned.length > 0 && (
          <div style={{ background:'rgba(167,139,250,0.08)', border:'1px solid rgba(167,139,250,0.25)', borderRadius:10, padding:'12px 16px', marginBottom:16, display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
            <div style={{ fontSize:13, color:C.purple }}>✍️ You have {hasUnsigned.length} contract{hasUnsigned.length>1?'s':''} waiting for your signature</div>
            <button onClick={() => setSelOrder(hasUnsigned[0])} style={{ padding:'7px 14px', borderRadius:7, border:'none', background:C.purple, color:'#fff', fontWeight:700, fontSize:12, cursor:'pointer' }}>Review →</button>
          </div>
        )}

        {/* Stats */}
        {stats && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:10, marginBottom:20 }}>
            {[
              { label:'Total Rentals',    val:stats.totalOrders,           color:C.text,   icon:'📋' },
              { label:'Total Spent',      val:fmt(stats.totalSpent),       color:C.green,  icon:'💰' },
              { label:'Balance Due',      val:fmt(stats.totalDue),         color:stats.totalDue>0?C.red:C.green, icon:'⏳' },
            ].map(({label,val,color,icon})=>(
              <div key={label} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'14px 16px' }}>
                <div style={{ fontSize:20, marginBottom:4 }}>{icon}</div>
                <div style={{ fontSize:18, fontWeight:800, color, lineHeight:1 }}>{val}</div>
                <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>{label}</div>
              </div>
            ))}
          </div>
        )}
        {stats?.favoriteItem && (
          <div style={{ background:'rgba(249,115,22,0.06)', border:`1px solid rgba(249,115,22,0.2)`, borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:12, color:C.muted, display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:16 }}>⭐</span>
            <span>Most rented: <strong style={{ color:C.accent }}>{stats.favoriteItem}</strong></span>
          </div>
        )}

        {/* Tabs */}
        <div style={{ display:'flex', gap:4, marginBottom:20, borderBottom:`1px solid ${C.border}`, paddingBottom:0 }}>
          {[
            ['orders', `📋 All Orders (${orders.length})`],
            ['active', `🚀 Active (${activeOrders.length})`],
            ['history', `✅ History (${orders.filter(o=>o.status==='returned').length})`],
            ['documents', `📄 Documents`],
          ].map(([v,l]) => (
            <button key={v} onClick={() => setTab(v)}
              style={{ padding:'9px 14px', borderRadius:'8px 8px 0 0', border:`1px solid ${tab===v?C.border:'transparent'}`, borderBottom:tab===v?`1px solid ${C.card}`:'none', background:tab===v?C.card:'transparent', color:tab===v?C.text:C.muted, fontWeight:tab===v?700:400, fontSize:12, cursor:'pointer', marginBottom:tab===v?-1:0 }}>
              {l}
            </button>
          ))}
        </div>

        {/* Documents tab */}
        {tab === 'documents' && (
          <div>
            {orders.filter(o => o.signed_at || o.contract_pdf_url).length === 0 ? (
              <div style={{ textAlign:'center', padding:'60px 20px', color:C.muted }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📄</div>
                <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:6 }}>No signed documents yet</div>
                <div style={{ fontSize:13 }}>Signed contracts will appear here</div>
              </div>
            ) : (
              <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                {orders.filter(o => o.signed_at).map(o => (
                  <div key={o.id} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 20px', display:'flex', alignItems:'center', justifyContent:'space-between', gap:12 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:12 }}>
                      <div style={{ width:40, height:40, borderRadius:10, background:'rgba(96,165,250,0.1)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20 }}>📋</div>
                      <div>
                        <div style={{ fontSize:13, fontWeight:700, color:C.text }}>Rental Contract — Order #{o.id}</div>
                        <div style={{ fontSize:11, color:C.muted }}>Signed {new Date(o.signed_at).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'})}</div>
                        <div style={{ fontSize:11, color:C.muted }}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</div>
                      </div>
                    </div>
                    <a href={`${API}/api/rental-portal/orders/${o.id}/contract`}
                      onClick={async(e)=>{ e.preventDefault(); const r = await fetch(`${API}/api/rental-portal/orders/${o.id}/contract`,{headers:{'Authorization':`Bearer ${token}`}}); const blob = await r.blob(); const url = URL.createObjectURL(blob); window.open(url,'_blank'); }}
                      style={{ padding:'8px 16px', borderRadius:8, background:'rgba(96,165,250,0.1)', border:`1px solid rgba(96,165,250,0.3)`, color:'#60a5fa', fontSize:12, fontWeight:700, cursor:'pointer', textDecoration:'none', whiteSpace:'nowrap' }}>
                      ⬇ Download PDF
                    </a>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History tab */}
        {tab === 'history' && (() => {
          const returned = orders.filter(o => o.status === 'returned');
          if (returned.length === 0) return (
            <div style={{ textAlign:'center', padding:'60px 20px', color:C.muted }}>
              <div style={{ fontSize:40, marginBottom:12 }}>✅</div>
              <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:6 }}>No completed rentals yet</div>
              <div style={{ fontSize:13 }}>Your rental history will appear here</div>
            </div>
          );
          const totalSpentHist = returned.reduce((s,o)=>s+Number(o.amount_paid||0),0);
          return (
            <div>
              <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'12px 16px', marginBottom:14, display:'flex', gap:16, fontSize:13 }}>
                <span style={{ color:C.muted }}>{returned.length} completed rental{returned.length!==1?'s':''}</span>
                <span style={{ color:C.green, fontWeight:700 }}>{fmt(totalSpentHist)} total spent</span>
              </div>
              {returned.map(o => {
                const balance = Math.max(0, Number(o.total||0) - Number(o.amount_paid||0));
                return (
                  <div key={o.id} onClick={() => setSelOrder(o)} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 20px', marginBottom:10, cursor:'pointer', opacity:0.85 }}
                    onMouseEnter={e=>{ e.currentTarget.style.opacity='1'; e.currentTarget.style.borderColor='#3a3f60'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.opacity='0.85'; e.currentTarget.style.borderColor=C.border; }}>
                    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                      <div style={{ flex:1 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                          <span style={{ fontSize:13, fontWeight:800, color:C.text }}>Order #{o.id}</span>
                          <span style={{ padding:'3px 10px', borderRadius:20, fontSize:10, fontWeight:700, background:'rgba(52,211,153,0.15)', color:C.green }}>✓ Completed</span>
                          {o.signed_at && (
                            <a href="#" onClick={async(e)=>{ e.stopPropagation(); e.preventDefault(); const r = await fetch(`${API}/api/rental-portal/orders/${o.id}/contract`,{headers:{'Authorization':`Bearer ${token}`}}); const blob = await r.blob(); const url = URL.createObjectURL(blob); window.open(url,'_blank'); }}
                              style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, background:'rgba(96,165,250,0.1)', color:'#60a5fa', textDecoration:'none' }}>
                              📋 Contract
                            </a>
                          )}
                        </div>
                        <div style={{ fontSize:12, color:C.muted, display:'flex', gap:12, flexWrap:'wrap' }}>
                          <span>📅 {fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
                          {(o.rental_order_items||[]).length > 0 && <span>📦 {o.rental_order_items.map(i=>i.item_name).join(', ')}</span>}
                        </div>
                      </div>
                      <div style={{ textAlign:'right', flexShrink:0 }}>
                        <div style={{ fontSize:18, fontWeight:800, color:C.text }}>{fmt(o.total)}</div>
                        <div style={{ fontSize:11, color:balance>0?C.red:C.green, fontWeight:700 }}>{balance>0?`${fmt(balance)} due`:'✓ Paid'}</div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Orders list (All + Active tabs) */}
        {(tab === 'orders' || tab === 'active') && (() => {
          const display = tab === 'active' ? activeOrders : orders;
          if (display.length === 0) {
            return (
              <div style={{ textAlign:'center', padding:'60px 20px', color:C.muted }}>
                <div style={{ fontSize:40, marginBottom:12 }}>📋</div>
                <div style={{ fontSize:15, fontWeight:600, color:C.text, marginBottom:6 }}>{tab==='active'?'No active rentals':'No orders yet'}</div>
                <div style={{ fontSize:13 }}>Your rental history will appear here</div>
              </div>
            );
          }
          return display.map(o => {
            const balance = Math.max(0, Number(o.total||0) - Number(o.amount_paid||0));
            return (
              <div key={o.id} onClick={() => setSelOrder(o)} style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:'16px 20px', marginBottom:10, cursor:'pointer', transition:'border-color 0.15s' }}
                onMouseEnter={e=>e.currentTarget.style.borderColor='#3a3f60'}
                onMouseLeave={e=>e.currentTarget.style.borderColor=C.border}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                      <span style={{ fontSize:13, fontWeight:800, color:C.text }}>Order #{o.id}</span>
                      <span style={{ padding:'3px 10px', borderRadius:20, fontSize:10, fontWeight:700, background:`${STATUS_COLOR[o.status]}20`, color:STATUS_COLOR[o.status] }}>{STATUS_LABEL[o.status]}</span>
                      {!o.signed_at && o.contract_pdf_url && <span style={{ padding:'3px 8px', borderRadius:20, fontSize:10, fontWeight:700, background:'rgba(167,139,250,0.15)', color:C.purple }}>✍️ Sign</span>}
                      {o.signed_at && (
                        <a href="#" onClick={async(e)=>{ e.stopPropagation(); e.preventDefault(); const r = await fetch(`${API}/api/rental-portal/orders/${o.id}/contract`,{headers:{'Authorization':`Bearer ${token}`}}); const blob = await r.blob(); const url = URL.createObjectURL(blob); window.open(url,'_blank'); }}
                          style={{ padding:'2px 8px', borderRadius:20, fontSize:10, fontWeight:700, background:'rgba(96,165,250,0.1)', color:'#60a5fa', textDecoration:'none' }}>
                          📋 Contract
                        </a>
                      )}
                    </div>
                    <div style={{ fontSize:12, color:C.muted, display:'flex', gap:12, flexWrap:'wrap' }}>
                      <span>📅 {fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
                      {o.fulfillment_type && <span>🚚 {o.fulfillment_type.charAt(0).toUpperCase()+o.fulfillment_type.slice(1)}</span>}
                      {(o.rental_order_items||[]).length > 0 && <span>📦 {o.rental_order_items.length} item{o.rental_order_items.length>1?'s':''}</span>}
                    </div>
                  </div>
                  <div style={{ textAlign:'right', flexShrink:0 }}>
                    <div style={{ fontSize:18, fontWeight:800, color:C.text }}>{fmt(o.total)}</div>
                    {balance > 0 ? (
                      <div style={{ fontSize:11, color:C.red, fontWeight:700 }}>{fmt(balance)} due</div>
                    ) : (
                      <div style={{ fontSize:11, color:C.green, fontWeight:700 }}>✓ Paid</div>
                    )}
                  </div>
                </div>
              </div>
            );
          });
        })()}
      </div>
    </div>
    </>
  );
}

function Header({ business, client, onLogout, onAccountSettings }) {
  const [showMenu, setShowMenu] = useState(false);
  return (
    <div style={{ background:C.card, borderBottom:`1px solid ${C.border}`, padding:'0 20px', position:'sticky', top:0, zIndex:100 }}>
      <div style={{ maxWidth:720, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center', height:56 }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <span style={{ fontSize:20 }}>🏗️</span>
          <span style={{ fontSize:14, fontWeight:800, color:C.text }}>{business?.name || 'Client Portal'}</span>
        </div>
        <div style={{ position:'relative' }}>
          <button onClick={() => setShowMenu(m=>!m)}
            style={{ display:'flex', alignItems:'center', gap:8, padding:'6px 12px', borderRadius:8, border:`1px solid ${C.border}`, background:'transparent', color:C.text, cursor:'pointer', fontSize:13 }}>
            <div style={{ width:26, height:26, borderRadius:'50%', background:'rgba(249,115,22,0.15)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:C.accent }}>
              {(client?.name||'?').charAt(0).toUpperCase()}
            </div>
            <span>{client?.name?.split(' ')[0] || 'Account'}</span>
            <span style={{ fontSize:10, color:C.muted }}>{showMenu?'▲':'▼'}</span>
          </button>
          {showMenu && (
            <div style={{ position:'absolute', right:0, top:'calc(100% + 8px)', background:C.card, border:`1px solid ${C.border}`, borderRadius:10, padding:'6px', minWidth:180, boxShadow:'0 8px 24px rgba(0,0,0,0.4)', zIndex:200 }}>
              <div style={{ padding:'8px 12px', borderBottom:`1px solid ${C.border}`, marginBottom:4 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.text }}>{client?.name}</div>
                <div style={{ fontSize:11, color:C.muted }}>{client?.email}</div>
                {client?.is_business_user && (
                  <div style={{ marginTop:4, display:'flex', alignItems:'center', gap:5 }}>
                    <span style={{ fontSize:10, background:'rgba(249,115,22,0.15)', color:C.accent, padding:'2px 7px', borderRadius:4, fontWeight:700, textTransform:'capitalize' }}>{client?.role || 'member'}</span>
                    {client?.role === 'admin' && (
                      <span style={{ fontSize:10, color:C.muted, cursor:'help' }} title="Can view and manage all orders under this business account">ⓘ</span>
                    )}
                    <span style={{ fontSize:10, color:C.muted }}>· {client?.company}</span>
                  </div>
                )}
              </div>
              <button onClick={() => { setShowMenu(false); onAccountSettings(); }}
                style={{ width:'100%', padding:'8px 12px', background:'none', border:'none', color:C.text, fontSize:13, cursor:'pointer', textAlign:'left', borderRadius:6, display:'flex', alignItems:'center', gap:8 }}>
                ⚙️ Account Settings
              </button>
              <button onClick={onLogout}
                style={{ width:'100%', padding:'8px 12px', background:'none', border:'none', color:C.red, fontSize:13, cursor:'pointer', textAlign:'left', borderRadius:6, display:'flex', alignItems:'center', gap:8 }}>
                🚪 Sign Out
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
